import { useCallback } from 'react';
import { CloseOutlined } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { SignatureType, UploadPath } from 'queries';

import LinearWithValueLabel from 'common/progress';

import { signatureTypeSelectOptions, uploadPathSelectOptions } from '../bulk-upload.constants';
import { BulkUploadAutocompleteSelect } from '../bulk-upload-autocomplete-select';

import { useStyles } from './bulk-upload-preview.styles';

type BulkUploadPreviewProps = {
  file: File;
  uploadProgress?: Record<string, number>;
  isUploading: boolean;
  removeFile: (file: File) => void;
  documentTagsList: { label: string; value: string }[];
  uploadPath?: UploadPath;
  signatureType: SignatureType | null;
  documentTag: string;
  onUploadPathChange: (value: UploadPath | undefined) => void;
  onDocumentTagChange: (value: string | undefined) => void;
  onSignatureTypeChange: (value: SignatureType) => void;
  isAdmin?: boolean;
};

export const BulkUploadPreview = ({
  file,
  uploadProgress,
  removeFile,
  isUploading,
  documentTagsList,
  uploadPath,
  onUploadPathChange,
  signatureType,
  documentTag,
  onDocumentTagChange,
  onSignatureTypeChange,
  isAdmin,
}: BulkUploadPreviewProps) => {
  const classes = useStyles();
  const fileName = file.name;
  const uploadPercentage = uploadProgress?.[fileName] ?? 0;
  const selectedUploadPath = uploadPathSelectOptions.find((option) => option.value === uploadPath);
  const selectedSignatureType = signatureTypeSelectOptions.find(
    (option) => option.value === signatureType,
  );
  const selectedDocumentTag = documentTagsList.find((option) => option.value === documentTag);

  const convertBytes = useCallback((bytes: number) => {
    if (bytes === 0) {
      return '0 Byte';
    }
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString(), 10);
    return `(${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]})`;
  }, []);

  return (
    <Box key={file.name} className={classes.content}>
      <Box className={classes.progressBar}>
        <Box className={classes.title}>
          <Typography className={classes.fileName}>{file.name}</Typography>
          <Typography className={classes.fileSize}>{convertBytes(file.size)}</Typography>
        </Box>
        <LinearWithValueLabel progress={uploadPercentage} />
        <Box className={classes.tagsInputs}>
          {isAdmin && (
            <>
              <BulkUploadAutocompleteSelect
                className={classes.tagInputField}
                options={uploadPathSelectOptions}
                label="Upload path"
                value={selectedUploadPath ?? null}
                onChange={(selected) => onUploadPathChange(selected?.value)}
              />
              <BulkUploadAutocompleteSelect
                className={classes.tagInputField}
                options={signatureTypeSelectOptions}
                label="Signature type"
                value={selectedSignatureType ?? null}
                onChange={(selected) =>
                  onSignatureTypeChange(selected?.value ?? SignatureType.NoSignature)
                }
              />
            </>
          )}
          <BulkUploadAutocompleteSelect
            className={classes.tagInputField}
            options={documentTagsList}
            value={selectedDocumentTag ?? null}
            onChange={(selected) => onDocumentTagChange(selected?.value)}
            label="Document tag"
          />
        </Box>
      </Box>
      {!isUploading && (
        <IconButton onClick={() => removeFile(file)} disabled={isUploading}>
          <CloseOutlined />
        </IconButton>
      )}
    </Box>
  );
};
